import React, { useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import Main from "../../components/layout/Main";
import AlertDialog from "../../components/layout/app/AlertDialog";
import { useToggleTop } from "../../components/common/ToggleTop";
import { apiMessage } from "../../api/apiMessage";
import useAlive from "../../hooks/useAlive";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import DevicePicker from "./DevicePicker";
import { ShareOptions, VoiceToggles } from "./parts";
import { SHARE_PATHS, asList, sameDevices, sharedMessage, voiceOn } from "./shareUtils";
import "../../assets/styles/app-share.css";

// The app's Update Sharing screen (app/screens/ShareProperty/SharedUserScreen.js):
// change the role, devices and voice assistants of one person on the Shared
// Users list, or take the property away from them. Opened with { share } from
// that list.
function SharedUser() {
  const { state } = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const propertyId = User.getPropertyId();
  const opened = state?.share ?? null;

  // share is the record as saved; the fields below are what's on the page.
  const [share, setShare] = useState(opened);
  const [role, setRole] = useState(opened?.role ?? "");
  const [shareAll, setShareAll] = useState(opened?.shareAll === true);
  const [deviceIds, setDeviceIds] = useState(opened?.devices ?? []);
  const [shareWithGoogle, setShareWithGoogle] = useState(voiceOn(opened?.shareWithGoogle));
  const [shareWithAlexa, setShareWithAlexa] = useState(voiceOn(opened?.shareWithAlexa));
  const [devices, setDevices] = useState(null);
  const [gone, setGone] = useState(false);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);
  const touched = useRef(false);

  // The page opens with the list's copy of the share. Read the saved one, and
  // the devices to choose from; edits made meanwhile are kept.
  useEffect(() => {
    if (!opened || !propertyId) return undefined;
    let current = true;
    UserService.getConfig(propertyId)
      .then((config) => current && setDevices(config?.devices ?? []))
      .catch(() => {
        if (!current) return;
        setDevices([]);
        toast("Couldn't load the property's devices.", "red");
      });
    UserService.getSharedUsers(propertyId)
      .then((shared) => {
        if (!current) return;
        const latest = asList(shared).find((item) => item.id === opened.id);
        if (!latest) {
          setGone(true);
          return;
        }
        setShare(latest);
        if (touched.current) return;
        setRole(latest.role ?? "");
        setShareAll(latest.shareAll === true);
        setDeviceIds(latest.devices ?? []);
        setShareWithGoogle(voiceOn(latest.shareWithGoogle));
        setShareWithAlexa(voiceOn(latest.shareWithAlexa));
      })
      .catch(() => {
        // Keep the list's copy.
      });
    return () => {
      current = false;
    };
    // Loads once for the share the page was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!opened) return <Redirect to={SHARE_PATHS.HOME} />;

  const edit = (setter) => (value) => {
    touched.current = true;
    setter(value);
  };

  const changed =
    role !== (share.role ?? "") ||
    shareAll !== (share.shareAll === true) ||
    (!shareAll && !sameDevices(deviceIds, share.devices)) ||
    shareWithGoogle !== voiceOn(share.shareWithGoogle) ||
    shareWithAlexa !== voiceOn(share.shareWithAlexa);
  const canSave = changed && Boolean(role) && (shareAll || deviceIds.length > 0);

  const save = async (event) => {
    event.preventDefault();
    if (!canSave || busy) return;
    const update = { id: share.id, role, devices: shareAll ? [] : deviceIds, shareAll, shareWithGoogle, shareWithAlexa };
    setBusy(true);
    try {
      await UserService.updateSharing(update, propertyId);
      if (!alive.current) return;
      setShare({ ...share, ...update });
      setDialog({ type: "success", title: "Shared Successfully!", message: sharedMessage(shareAll, deviceIds.length, share.grantedTo) });
    } catch (error) {
      if (alive.current) {
        setDialog({ type: "error", title: "Could not update sharing", message: apiMessage(error, "Updating failed. Please try again.") });
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const remove = async () => {
    setDialog(null);
    setBusy(true);
    try {
      await UserService.deleteSharing(propertyId, share.id);
    } catch (error) {
      if (alive.current) {
        setBusy(false);
        setDialog({ type: "error", title: "Could not remove user", message: apiMessage(error, "Removing failed. Please try again.") });
      }
      return;
    }
    toast(`${share.grantedTo} no longer has access to this property.`, "green");
    history.goBack();
  };

  return (
    <Main header="stack" title="Share Property" hideTabBar>
      <div className="hx-share">
        {gone ? (
          <div className="hx-empty" role="alert">
            {share.grantedTo} no longer has access to this property.
            <button type="button" className="hx-btn" onClick={() => history.goBack()}>
              Back
            </button>
          </div>
        ) : (
          <div className="hx-share-card">
            <h2 className="hx-share-card-title">Update Sharing</h2>
            <form className="hx-share-form" onSubmit={save} noValidate>
              <div className="hx-field">
                <label className="hx-field-label" htmlFor="hx-share-user">
                  User
                </label>
                <input id="hx-share-user" className="hx-field-input is-readonly" value={share.grantedTo} readOnly />
              </div>

              <ShareOptions
                idPrefix="hx-share-edit"
                role={role}
                onRole={edit(setRole)}
                shareAll={shareAll}
                onShareAll={edit(setShareAll)}
                deviceCount={deviceIds.length}
                onPickDevices={() => setPicking(true)}
              />

              <VoiceToggles
                idPrefix="hx-share-edit"
                google={shareWithGoogle}
                alexa={shareWithAlexa}
                onGoogle={edit(setShareWithGoogle)}
                onAlexa={edit(setShareWithAlexa)}
              />

              <button type="submit" className="hx-share-btn" disabled={!canSave || busy}>
                SAVE
              </button>
              <button
                type="button"
                className="hx-share-btn is-danger"
                disabled={busy}
                onClick={() =>
                  setDialog({
                    type: "confirmation",
                    title: "Remove User",
                    message: `Are you sure you want to remove "${share.grantedTo}" from this property?`,
                    onConfirm: remove,
                  })
                }
              >
                DELETE
              </button>
            </form>
          </div>
        )}
      </div>

      <DevicePicker
        open={picking}
        devices={devices}
        selected={deviceIds}
        onCancel={() => setPicking(false)}
        onSave={(ids) => {
          edit(setDeviceIds)(ids);
          setPicking(false);
        }}
      />

      <AlertDialog
        open={Boolean(dialog)}
        type={dialog?.type}
        title={dialog?.title}
        message={dialog?.message}
        onClose={() => setDialog(null)}
        onConfirm={dialog?.onConfirm}
      />
    </Main>
  );
}

export default SharedUser;
